import { Field, Float, ID, InputType, ObjectType, registerEnumType } from 'type-graphql'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import { BaseEntity, generateEntityId, RelatedEntity, User } from '@ztechapps/common.contract'
import { ResultStatusEnum } from '../enums/ResultStatusEnum'
import { Assessment } from './Assessment'
import { ScanBatch } from './ScanBatch'

// Registered beside the fields that use them so the enum files stay decorator-free for the mobile apps.
registerEnumType(ResultStatusEnum, {
  name: 'ResultStatusEnum',
  description: 'Review status of a graded assessment result',
})

@ObjectType()
@InputType('ScannedPageInput')
export class ScannedPage {
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  pageNumber: number

  @Field(() => String)
  @Prop({ type: String, required: true })
  imageUrl: string

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  aiConfidence?: number

  @Field(() => Date, { nullable: true })
  @Prop({ type: Date })
  processedAt?: Date
}

@ObjectType()
@InputType('StudentAnswerInput')
export class StudentAnswer {
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  questionNumber: number

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  extractedAnswer?: string

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  confidence?: number

  /**
   * Tri-state grade: true (correct), false (wrong), or null (ungradable —
   * either side of the comparison was empty).
   */
  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean })
  isCorrect?: boolean

  /**
   * The teacher's manual score for an open range, out of the range's points
   * total. Set alongside manuallyEntered when the teacher scores the range;
   * cleared by resetToAuto since an open item can never be re-derived by
   * auto-grading.
   */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  awardedPoints?: number

  @Field(() => Boolean)
  @Prop({ type: Boolean, default: false })
  needsReview: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean, default: false })
  manuallyEntered: boolean

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  sourceImageCrop?: string

  /**
   * A teacher's free-text explanation for a manual grade override. Set
   * alongside manuallyEntered on an override, cleared alongside it on reset.
   */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  remarks?: string
}

@ObjectType('AssessmentResult')
@InputType('AssessmentResultInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'assessmentresults' } })
export class AssessmentResult extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ares') })
  declare _id: string

  /**
   * The parent assessment. Ownership is reached through this relation, and is
   * also denormalized onto `owner` for synchronous owner-scoped queries.
   */
  @Field(() => Assessment)
  @Prop({ type: RelatedEntity })
  assessment: ReturnType<() => Assessment>

  /**
   * Back-reference to the ScanBatch that created this result via the multi-image batch scan
   * flow. Absent for results created via the single-image ProcessScan flow. Lets a retried
   * batch job find and delete any results a prior crashed attempt already wrote, before
   * recreating them (exactly-once semantics).
   */
  @Field(() => ScanBatch, { nullable: true })
  @Prop({ type: RelatedEntity })
  scanBatch?: ReturnType<() => ScanBatch>

  /**
   * Denormalized owner (the teacher who owns the parent assessment). Stamped on
   * create so every tenancy-scoped read/write can isolate by owner within the
   * shared DFT tenant (mirrors Assessment.owner).
   */
  @Field(() => User)
  @Prop({ type: RelatedEntity })
  owner: ReturnType<() => User>

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  studentName?: string

  /**
   * True once a teacher has manually corrected this result's name. A later
   * re-scan of the same sheet must leave the stored name untouched while this
   * is true, instead of overwriting it with a fresh AI guess.
   */
  @Field(() => Boolean)
  @Prop({ type: Boolean, default: false })
  studentNameManuallyEntered: boolean

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  totalScore: number

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  totalItems: number

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  percentage: number

  @Field(() => ResultStatusEnum)
  @Prop({ type: String, enum: ResultStatusEnum, required: true, default: ResultStatusEnum.PendingReview })
  declare status: ResultStatusEnum

  @Field(() => [ScannedPage])
  @Prop({ type: [ScannedPage] }, PropType.ARRAY)
  pages: ReturnType<() => ScannedPage[]>

  @Field(() => [StudentAnswer])
  @Prop({ type: [StudentAnswer] }, PropType.ARRAY)
  answers: ReturnType<() => StudentAnswer[]>
}
